import { A11yModule } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  signal
} from '@angular/core';

import { GoalCelebrationService } from '@cpd-app/core-module/services/goal-celebration/goal-celebration.service';
import { focusRovingItem, rovingIndex } from '@cpd-app/shared-module/utils/roving-keys';

/*
 * CRED.1 adds 'credential'. It is a Credential Registry credential set from the
 * occupation page, and it is NOT 'program': that one is a school's academic
 * programme, which CPD's own code confusingly calls a credential-picker.
 */
export type GoalKey =
  | 'career'
  | 'credential'
  | 'program'
  | 'school'
  | 'licensure';

export interface GoalRow {
  key: GoalKey;
  label: string;
  /** What is set, or empty when nothing is. */
  value: string;
  /** The one the member does next. Only ever one. */
  isNext?: boolean;
  /**
   * Set on a goal the report does not need.
   *
   * The licensure row sat in the same numbered checklist as the three the
   * report is built from, with the same solid marker and the same "None
   * selected", so it read as a fourth thing the member had failed to do. It is
   * not: `isComplete` below has never counted it, and neither does the Next
   * pointer. The row now says on its face what the logic already believed.
   */
  isOptional?: boolean;
}

/**
 * WALK-70 (approved 09-29). The leaving-or-staying question as a REQUIRED
 * step: its own light blue-grey ground, the #17-18 red asterisk, 14px type,
 * and the dialog's main button waits for an answer as Open your Goals Report
 * already did. False restores the previous section and behavior.
 */
export const STAY_OR_LEAVE_V2 = true;

/** Whether the member is leaving service or staying in it. */
export type ServiceIntent = 'transition' | 'stay' | '';

export interface MilitaryOccupation {
  code: string;
  title: string;
  branch: string;
  /** WALK-71. Already saved as a goal (a favorite military occupation). */
  isFavorite?: boolean;
}

/**
 * What happens between one goal and the next.
 *
 * Setting a goal is the moment to say what it changed and what is left, so the
 * dialog carries the whole checklist rather than a line of praise: a member who
 * has just set a program goal is deciding whether to carry on to a school,
 * and that decision needs the state of all four in front of them.
 *
 * It also asks the one question that setting a civilian goal raises and nothing
 * else on the page does: a civilian goal does not have to mean separating, and
 * a member who is staying in still has Tuition Assistance and still has a
 * credential path.
 *
 * GR-13 removed the related-military-occupations list that used to follow that
 * question. Nothing about it was broken; offering a reclassification in the
 * middle of confirming a goal just asks the wrong thing at the wrong moment.
 *
 * GR-15. STANDALONE, so the 3/3 counter in the top bar can raise it.
 *
 * It was declared in `AcademicGoalModule` and mounted in exactly one template,
 * so the header could not reach it and opened the legacy dialog instead. The
 * header lives in the core module, and importing a lazy feature module there to
 * get one component would drag the whole academic-goal tree into the eager
 * bundle.
 *
 * Standalone with an EMPTY imports list, which is not a shortcut: after GR-13
 * this template uses no components, no directives and no pipes -- only built-in
 * control flow. Nothing had to be untangled to make it portable.
 */
@Component({
  selector: 'cpd-goal-set-dialog',
  standalone: true,
  // A11Y.1: A11yModule for cdkTrapFocus, and nothing else.
  imports: [A11yModule],
  templateUrl: './goal-set-dialog.component.html',
  styleUrls: ['./goal-set-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GoalSetDialogComponent implements AfterViewInit, OnDestroy {
  /** Which goal was just set, which is what the heading reports. */
  @Input() public justSet: GoalKey | null = null;
  @Input() public goals: GoalRow[] = [];
  @Input() public intent: ServiceIntent = '';
  /** Related occupations, offered once "staying in" is the answer. */
  /**
   * Whether related occupations can be matched at all. They come from the
   * skills survey, so without it there is nothing to match against and the
   * section says so instead of showing an empty shelf.
   */
  /**
   * Whether the member's skills survey is complete, or null while we do not
   * yet know.
   *
   * Tri-state on purpose. This was a plain boolean seeded false, so for one
   * frame every member - including one who finished the survey months ago -
   * was told to go and take it.
   */
  @Input() public canMatchOccupations: boolean | null = null;
  /**
   * CPD-667. Whether the member is serving on ACTIVE DUTY, or null while we
   * do not know.
   *
   * CPD-661 narrowed "Are you leaving, or staying in?" from everybody to
   * military members, which fixed the civilian who could not reach their
   * report. It did not go far enough. "Military" here meant any branch that
   * is not Civilian, and `api.militaryservetype` has ten rows: Active Duty,
   * Veteran, and eight Guard/Reserve components. A Veteran has already left,
   * so both answers are false for them and the gate cannot be satisfied
   * honestly -- which is exactly what QA hit, having said so in the skills
   * survey one step earlier.
   *
   * The question presumes a current obligation to leave or keep. Only active
   * duty has one, so only active duty is asked.
   *
   * Tri-state for the same reason `canMatchOccupations` is: a plain boolean
   * seeded false shows the wrong thing for one frame. Only an explicit true
   * shows the question, so an input nobody passes asks nobody -- the safe
   * direction, since the failure is a question not asked rather than a
   * member held at a gate that does not fit them.
   */
  @Input() public isActiveDuty: boolean | null = null;
  /** Occupations the member has said they are interested in. */

  @Output() public readonly closed = new EventEmitter<void>();
  @Output() public readonly intentPicked = new EventEmitter<ServiceIntent>();
  @Output() public readonly goalRemoved = new EventEmitter<GoalKey>();
  @Output() public readonly continued = new EventEmitter<void>();
  @Output() public readonly reportOpened = new EventEmitter<void>();

  /**
   * WALK-71. The military occupations that lead to the career goal, shown
   * when the member answers "staying in". Null means the host does not offer
   * them (the shell's copy of this dialog), and the block is not drawn.
   */
  @Input() public relatedOccupations: MilitaryOccupation[] | null = null;
  /** The career goal's title, for the block's heading. */
  @Input() public careerTitle = '';
  /** WALK-71 review. The list is loading: say so, never "none". */
  @Input() public relatedLoading = false;
  /**
   * WALK-70 review. Whether the member's saved answer has been read. While it
   * has not, an empty `intent` is "not known yet", and Continue is not held
   * on it. Hosts that do not pass it keep the previous behavior.
   */
  @Input() public intentLoaded: boolean | null = true;
  /** One pressed: the host saves or unsaves it as a goal. */
  @Output() public readonly occupationToggled = new EventEmitter<MilitaryOccupation>();

  /** WALK-70. See STAY_OR_LEAVE_V2. */
  public readonly intentV2 = STAY_OR_LEAVE_V2;

  /** Shown once they try to continue without answering. */
  public readonly needsAnswer = signal(false);

  @ViewChild('dialog') private dialogEl?: ElementRef<HTMLElement>;
  // Not '#heading': that would shadow the `heading` getter in the template.
  @ViewChild('headingEl') private headingEl?: ElementRef<HTMLElement>;

  /** Whatever had focus when the dialog opened. */
  private opener: Element | null = null;
  /** GoalCelebrationService.returnFocusTo as it was when this dialog opened. */
  private returnFocusTo: HTMLElement | null = null;

  /**
   * Optional and trailing, so the specs that build this with `new` still
   * compile and run (an inject() field would throw NG0203 there).
   */
  public constructor(private readonly celebration?: GoalCelebrationService) {}

  /**
   * A11Y.1 (28 Sep 508 rescan, 2.4.3). FOCUS MOVES IN WHEN THE DIALOG OPENS.
   *
   * It never did: the dialog appeared over the page and focus stayed on the
   * page behind it, so a keyboard or screen reader user did not know it was
   * there. It goes to the heading, which names the dialog, rather than to the
   * first control, which is Close.
   *
   * Done here rather than with cdkTrapFocusAutoCapture: that restores focus
   * to whatever had it at open, and on the occupation page nothing did (see
   * GoalCelebrationService.returnFocusTo), so it would restore to the page.
   */
  public ngAfterViewInit(): void {
    this.opener = typeof document === 'undefined' ? null : document.activeElement;
    this.returnFocusTo = this.celebration?.returnFocusTo ?? null;
    this.headingEl?.nativeElement.focus();
  }

  /**
   * AND GOES BACK WHEN IT CLOSES, to the control that opened it.
   *
   * By the time this runs the dialog's DOM is gone, and focus with it, so
   * the page's focus is on BODY (the rescan's finding). Only then is it put
   * back: if Continue or Open your Goals Report moved focus somewhere on
   * purpose, it stays there.
   */
  public ngOnDestroy(): void {
    if (typeof document === 'undefined') {
      return;
    }
    const active = document.activeElement;
    const root = this.dialogEl?.nativeElement;
    const focusIsLost = !active || active === document.body || !!root?.contains(active);
    if (!focusIsLost) {
      return;
    }
    // Last, another modal still open underneath (a drawer this dialog was
    // raised over): focus stays inside it rather than falling to the page.
    // One that is inert or hidden is not open to anyone, so it is skipped.
    const underneath = Array.from(
      document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]')
    ).filter(
      (el) =>
        el !== root &&
        !root?.contains(el) &&
        !el.closest('[inert], [aria-hidden="true"]')
    );
    const candidates = [this.returnFocusTo, this.opener, underneath[underneath.length - 1]];
    // Used once: the next dialog names its own target.
    this.returnFocusTo = null;
    if (this.celebration) {
      this.celebration.returnFocusTo = null;
    }
    // A candidate counts only if it actually took focus: one that is not
    // focusable yet (a step still being re-rendered) is passed over.
    for (const el of candidates) {
      if (
        !el ||
        el === document.body ||
        !el.isConnected ||
        typeof (el as HTMLElement).focus !== 'function' ||
        (el as HTMLButtonElement).disabled
      ) {
        continue;
      }
      (el as HTMLElement).focus();
      if (document.activeElement === el) {
        return;
      }
    }
  }

  /**
   * A11Y.1. ESCAPE CLOSES THE DIALOG WHEREVER FOCUS IS.
   *
   * Heard on the document, not the dialog: bound on the dialog it needed focus
   * inside first, which was exactly what never happened.
   *
   * Two exceptions, both for the Remove confirmation from #1790 that opens on
   * top of this dialog. Material's dialog closes itself on Escape and marks
   * the event handled (it listens on BODY, so it hears it before the document
   * does); and a keypress inside some OTHER dialog belongs to that dialog.
   * Either way Escape closes the confirmation only, and this dialog stays.
   */
  @HostListener('document:keydown.escape', ['$event'])
  public onEscape(event: KeyboardEvent): void {
    if (event.defaultPrevented) {
      return;
    }
    const root = this.dialogEl?.nativeElement;
    const target = event.target instanceof Element ? event.target : null;
    const otherDialog = target?.closest('[role="dialog"], [role="alertdialog"]');
    if (otherDialog && otherDialog !== root) {
      return;
    }
    this.closed.emit();
  }

  private static readonly NOUN: Record<GoalKey, string> = {
    career: 'career',
    credential: 'credential',
    program: 'program',
    school: 'school',
    licensure: 'licensure'
  };

  /**
   * A11Y.1 (28 Sep 2026). THE NEXT GOAL, WORKED OUT FROM WHAT IS SET.
   *
   * The first of career, program and school with nothing in it -- the rule
   * `goalRows` marks as Next. Worked out here rather than read from `isNext`,
   * so the words cannot disagree with the checklist beneath them when a
   * caller passes rows without the flag. Null once all three are set.
   */
  public get nextKey(): GoalKey | null {
    const required: GoalKey[] = ['career', 'program', 'school'];
    return (
      required.find(
        (key) => !this.goals.some((goal) => goal.key === key && !!goal.value)
      ) ?? null
    );
  }

  /**
   * What the dialog is called.
   *
   * A11Y.1 (28 Sep 2026, 2.4.6 / 3.3.2). Opened from "N/3 Goals" with nothing
   * just set, this said "Your goals are all set" at 0/3 and 1/3 as well, and
   * it is the first thing a screen reader hears now that focus opens on it.
   * It also congratulated a goal the member had since removed in the dialog.
   * It now says what is true; the two states that were already right keep
   * their words.
   */
  public get heading(): string {
    // Everything but the optional goals is set, so there is nothing left to name.
    if (this.isComplete) {
      return 'Your goals are all set';
    }
    const justSetRow = this.justSet
      ? this.goals.find((goal) => goal.key === this.justSet)
      : undefined;
    const stillSet = !!this.justSet && (!justSetRow || !!justSetRow.value);
    if (this.justSet && stillSet) {
      return `Congratulations on setting a ${GoalSetDialogComponent.NOUN[this.justSet]} goal`;
    }
    return this.nextKey === 'career'
      ? 'You have not set a career goal yet'
      : 'Your goals so far';
  }

  public get subheading(): string {
    if (this.isComplete) {
      return 'Your Goals Report has an occupation, a program and a school to work against.';
    }
    switch (this.nextKey) {
      case 'career':
        return 'Start with the occupation you are working toward. Your program and school goals are chosen for it.';
      case 'program':
        return 'Next, the program that prepares you for it. Programs are ranked by how directly they prepare you for the work.';
      case 'school':
        return 'Now the school that teaches it. Schools are only comparable once the program is fixed.';
      default:
        return 'Your Goals Report has what it needs to work against.';
    }
  }

  /**
   * Career, program and school all set. Licensure and credential are optional.
   *
   * CRED.1 DELIBERATELY DID NOT JOIN THIS LIST. A member's Goals Report is
   * complete without a Registry credential, and adding a fourth requirement
   * would have moved the finish line for every member who has already reached
   * it -- someone with all three set would suddenly read as incomplete.
   */
  public get isComplete(): boolean {
    return (['career', 'program', 'school'] as GoalKey[]).every((key) =>
      this.goals.some((goal) => goal.key === key && !!goal.value)
    );
  }

  public get primaryLabel(): string {
    if (this.isComplete) {
      return 'Open your Goals Report';
    }
    // Both hosts' `continued` handlers already send a member with no career
    // goal to the career goals page, so the label now says that is where it goes.
    switch (this.nextKey) {
      case 'career':
        return 'Explore and set a career goal';
      case 'school':
        return 'Choose a school';
      default:
        return 'Explore and set a program goal';
    }
  }

  public pickIntent(intent: ServiceIntent): void {
    this.needsAnswer.set(false);
    this.intentPicked.emit(intent);
  }

  /** The two answers, in the order they are drawn. */
  private static readonly INTENTS: ServiceIntent[] = ['transition', 'stay'];

  /** Roving tabindex: the picked answer, or the first when none is. */
  public intentTabIndex(intent: ServiceIntent): number {
    const current = this.intent || GoalSetDialogComponent.INTENTS[0];
    return intent === current ? 0 : -1;
  }

  /** Arrows, Home and End move between the answers and pick, as a click would. */
  public onIntentKey(event: KeyboardEvent): void {
    const intents = GoalSetDialogComponent.INTENTS;
    const radios = Array.from(
      (event.currentTarget as HTMLElement).querySelectorAll<HTMLElement>('[role="radio"]')
    );
    const focused = radios.indexOf(event.target as HTMLElement);
    const from = focused >= 0 ? focused : Math.max(intents.indexOf(this.intent), 0);
    const next = rovingIndex(event, from, intents.length);

    if (next === null) {
      return;
    }

    event.preventDefault();
    this.pickIntent(intents[next]);
    focusRovingItem(event.currentTarget as HTMLElement, '[role="radio"]', next);
  }

  /** Whether the leaving-or-staying question applies to this member at all. */
  public get asksServiceIntent(): boolean {
    return this.isActiveDuty === true;
  }

  /**
   * The Goals Report is the thing the question feeds, so it will not open
   * unanswered. Said in the dialog rather than as a disabled button with no
   * reason on it.
   *
   * CPD-661. The gate now follows the question. It used to be unconditional,
   * which held civilians behind an answer to something they were never the
   * right person to give.
   */
  public openReport(): void {
    if (this.asksServiceIntent && !this.intent) {
      this.needsAnswer.set(true);
      return;
    }
    this.reportOpened.emit();
  }

  public continueOn(): void {
    // WALK-70. Required means required: the main button waits too, once the
    // saved answer has been read (an unread answer is not a missing one).
    if (this.intentV2 && this.asksServiceIntent && !this.intent && this.intentLoaded !== false) {
      this.needsAnswer.set(true);
      return;
    }
    if (this.isComplete) {
      this.openReport();
      return;
    }
    this.continued.emit();
  }

  /**
   * Where focus goes once the member has answered a Remove's confirmation.
   *
   * Cancelled: back on that row's Remove. Removed: that button is about to
   * go, and focus went to the page with it (found in the 508 test run), so it
   * moves to the next row's Remove, or the one before, or the dialog's main
   * button. The career goal takes every other goal with it, so after that
   * the main button is the only control left standing.
   *
   * Called by the page that mounts the dialog, since the page owns the
   * removal. Waits a tick for the confirmation's own focus restore, and does
   * nothing if focus has since gone somewhere outside the dialog on purpose.
   */
  public focusAfterRemoval(key: GoalKey, removed: boolean): void {
    setTimeout(() => {
      const root = this.dialogEl?.nativeElement;
      const active = document.activeElement;
      if (!root?.isConnected || (active && active !== document.body && !root.contains(active))) {
        return;
      }
      const main = root.querySelector<HTMLElement>('.gh-go');
      const removeFor = (k: GoalKey) =>
        root.querySelector<HTMLElement>(`.gh-rm[data-goal-key="${k}"]`);

      if (!removed) {
        (removeFor(key) ?? main)?.focus();
        return;
      }
      if (key === 'career') {
        main?.focus();
        return;
      }
      const keys = this.goals.map((goal) => goal.key);
      const at = keys.indexOf(key);
      const after = keys.slice(at + 1).map(removeFor).find((b) => !!b);
      const before = keys.slice(0, Math.max(at, 0)).reverse().map(removeFor).find((b) => !!b);
      (after ?? before ?? main)?.focus();
    });
  }
}
